import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment, renderList as _renderList, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "explainer"
}
const _hoisted_2 = { class: "explainer-head" }
const _hoisted_3 = { class: "explainer-item" }
const _hoisted_4 = {
  key: 0,
  class: "explainer-pill"
}
const _hoisted_5 = { class: "explainer-rows" }
const _hoisted_6 = {
  key: 0,
  class: "explainer-hr"
}
const _hoisted_7 = {
  key: 1,
  class: "explainer-rows"
}

import { computed } from 'vue'
import { game } from '../../game/init'
import type { Item } from '../../game'
import type { DropPlan } from '../../game/systems/ui'
import type { ExplodeEffect } from '../../game/systems/shade'
import Row from './Row.vue'
import Chip from './Chip.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'Explainer',
  props: {
    plan: { type: [Object, null], required: true },
    hoveredItem: { type: [Object, null], required: true }
  },
  setup(__props: any) {

const props = __props

const item = computed<Item | null>(() => props.plan?.item ?? props.hoveredItem ?? null)
const isDragging = computed(() => !!game.ui.state.dragFrom)

// Drop row whenever dragging. For drag-to-self we still show effects (merge,
// explode, spawn) — only hide if the plan would do nothing.
const dropPlan = computed<DropPlan | null>(() => {
  const p = props.plan
  if (!p) return null
  if (p.source.key === p.target.key) {
    const hasEffect = !!(p.explode || p.merge || p.spawn)
    return hasEffect ? p : null
  }
  return p
})

// Swap of two same-id items is a visual no-op — show as "→ ∅" instead of ⇄.
const noopSwap = computed(() =>
  !!(dropPlan.value?.swap && dropPlan.value.swap.id === dropPlan.value.item.id),
)

// Drop refused by commitDrop's gates (source shaded, target shaded without
// explodeOnShade, or target shaded+occupied even with explodeOnShade).
const dropRefused = computed(() => {
  const p = dropPlan.value
  if (!p) return false
  if (p.source.shade > 0) return true
  if (p.target.shade > 0) {
    if (!p.item.data.shade?.explodeOnShade) return true
    if (p.target.item) return true
  }
  return false
})

// Whether the static-fallback default tap recipe currently fits the board.
const defaultTapFits = computed(() => {
  const recipe = defaultTapRecipe.value
  const it = item.value
  if (!recipe || !it?.position) return true
  const tile = game.grid.tryAt(it.position)
  if (!tile) return true
  return game.spawn.fits({ from: tile, drops: recipe.drops, range: recipe.dropRange })
})

// Tap explosion: from plan, or re-derived for shaded items where plan is suppressed.
const tapExplode = computed<ExplodeEffect | null>(() => {
  if (!item.value || isDragging.value || dropPlan.value) return null
  if (props.plan?.explode) return props.plan.explode
  if (!item.value.data.shade?.explodeOnShade || !item.value.position) return null
  const tile = game.grid.tryAt(item.value.position)
  if (!tile) return null
  const preview = game.shade.previewExplosion(item.value, tile)
  return preview && preview.affected.some(a => a.tile.shade > 0) ? preview : null
})

// Tap-spawn preview, only when not dragging, not exploding, and the preview
// actually has spawnable items (resource-only drops fall through to the
// recipe fallback so non-item drops like "gold" still render).
const tapPreview = computed(() => {
  if (!item.value || isDragging.value || dropPlan.value) return null
  if (item.value.data.shade?.explodeOnShade) return null
  const preview = game.item.previewTapSpawn(item.value)
  return preview && preview.spawn.length > 0 ? preview : null
})

const defaultTapRecipe = computed(() => item.value?.data.tap?.recipes[0] ?? null)

const itemEmoji = computed(() => {
  if (!item.value) return ''
  return (game.data.items as any)[item.value.id]?.display?.emoji ?? item.value.id
})

const charge = computed(() => {
  const it = item.value
  if (!it?.data.charge || it.charge === undefined) return null
  return { current: it.charge, max: it.data.charge.max }
})

const hasInfo = computed(() => {
  const it = item.value
  if (!it) return false
  return !!(
    it.data.merge?.recipes?.length ||
    it.data.charge?.recipes?.length ||
    it.data.tap?.recipes?.some(r => r.charge != null) ||
    it.data.shade?.pausedInShade ||
    it.data.shade?.explodeOnShade ||
    it.data.shade?.spawnShade
  )
})

function explodeDamage(e: ExplodeEffect): number {
  return e.affected
    .filter(a => a.damage > 0 && a.tile.shade > 0)
    .reduce((sum, a) => sum + Math.min(a.damage, a.tile.shade), 0)
}

function explodeCount(e: ExplodeEffect): number {
  return e.affected.filter(a => a.damage > 0 && a.tile.shade > 0).length
}

function countItems(ids: string[]): Record<string, number> {
  return ids.reduce<Record<string, number>>((m, id) => {
    m[id] = (m[id] ?? 0) + 1
    return m
  }, {})
}

function countSpawn(spawn: { itemId: string }[]): Record<string, number> {
  return countItems(spawn.map(s => s.itemId))
}

/** Resolve "next" placeholders in recipe drops to the chain's next item id. */
function resolvedDrops(drops: Record<string, number>, it: Item): Record<string, number> {
  const out: Record<string, number> = {}
  for (const [id, count] of Object.entries(drops)) {
    const resolved = id === 'next' ? it.data.chain?.next : id
    if (resolved) out[resolved] = (out[resolved] ?? 0) + count
  }
  return out
}

return (_ctx: any,_cache: any) => {
  return (item.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(itemEmoji.value), 1 /* TEXT */),
          (charge.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_4, "⚡ " + _toDisplayString(charge.value.current) + "/" + _toDisplayString(charge.value.max), 1 /* TEXT */))
            : _createCommentVNode("v-if", true)
        ]),
        _createCommentVNode(" State row: drop XOR tap. Drag-to-self renders nothing. "),
        _createElementVNode("div", _hoisted_5, [
          (dropPlan.value)
            ? (_openBlock(), _createBlock(Row, {
                key: 0,
                icon: "↧",
                disabled: dropRefused.value,
                arrow: dropPlan.value.swap && !noopSwap.value ? '⇄' : undefined
              }, {
                default: _withCtx(() => [
                  _createCommentVNode(" Explosion "),
                  (dropPlan.value.explode)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createVNode(Chip, {
                          from: "",
                          tone: "drop",
                          item: dropPlan.value.item.id
                        }, null, 8 /* PROPS */, ["item"]),
                        _createVNode(Chip, {
                          to: "",
                          tone: "warn",
                          icon: "💥"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(explodeDamage(dropPlan.value.explode)), 1 /* TEXT */)
                          ]),
                          _: 1 /* STABLE */
                        }),
                        _createVNode(Chip, {
                          to: "",
                          tone: "shade",
                          icon: "#"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(explodeCount(dropPlan.value.explode)), 1 /* TEXT */)
                          ]),
                          _: 1 /* STABLE */
                        })
                      ], 64 /* STABLE_FRAGMENT */))
                    : (dropPlan.value.merge)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _createCommentVNode(" Merge "),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(countItems(dropPlan.value.merge.consume.map(i => i.id)), (c, id) => {
                            return (_openBlock(), _createBlock(Chip, {
                              key: `mc${id}`,
                              from: "",
                              tone: "merge",
                              item: id,
                              count: c
                            }, null, 8 /* PROPS */, ["item", "count"]))
                          }), 128 /* KEYED_FRAGMENT */)),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(countSpawn(dropPlan.value.merge.spawn), (c, id) => {
                            return (_openBlock(), _createBlock(Chip, {
                              key: `ms${id}`,
                              to: "",
                              tone: "match",
                              item: id,
                              count: c
                            }, null, 8 /* PROPS */, ["item", "count"]))
                          }), 128 /* KEYED_FRAGMENT */))
                        ], 64 /* STABLE_FRAGMENT */))
                      : (dropPlan.value.spawn)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                            _createCommentVNode(" Drop-spawn (e.g. dropping a flower onto an empty tile fires its tap) "),
                            _createVNode(Chip, {
                              from: "",
                              tone: "drop",
                              item: dropPlan.value.item.id
                            }, null, 8 /* PROPS */, ["item"]),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(countSpawn(dropPlan.value.spawn), (c, id) => {
                              return (_openBlock(), _createBlock(Chip, {
                                key: `ds${id}`,
                                to: "",
                                tone: "spawn",
                                item: id,
                                count: c
                              }, null, 8 /* PROPS */, ["item", "count"]))
                            }), 128 /* KEYED_FRAGMENT */))
                          ], 64 /* STABLE_FRAGMENT */))
                        : (noopSwap.value)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                              _createCommentVNode(" Swap onto a same-id item is a visual no-op: just disappears. "),
                              _createVNode(Chip, {
                                from: "",
                                tone: "drop",
                                item: dropPlan.value.item.id
                              }, null, 8 /* PROPS */, ["item"]),
                              _createVNode(Chip, { to: "" }, {
                                default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                                  _createTextVNode("∅", -1 /* CACHED */)
                                ]))]),
                                _: 1 /* STABLE */
                              })
                            ], 64 /* STABLE_FRAGMENT */))
                          : (dropPlan.value.swap)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                                _createCommentVNode(" Swap with a different item "),
                                _createVNode(Chip, {
                                  from: "",
                                  tone: "drop",
                                  item: dropPlan.value.item.id
                                }, null, 8 /* PROPS */, ["item"]),
                                _createVNode(Chip, {
                                  to: "",
                                  tone: "drop",
                                  item: dropPlan.value.swap.id
                                }, null, 8 /* PROPS */, ["item"])
                              ], 64 /* STABLE_FRAGMENT */))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 5 }, [
                                _createCommentVNode(" Landing on empty "),
                                _createVNode(Chip, {
                                  from: "",
                                  tone: "drop",
                                  item: dropPlan.value.item.id
                                }, null, 8 /* PROPS */, ["item"]),
                                _createVNode(Chip, {
                                  to: "",
                                  tone: "drop",
                                  icon: "□"
                                })
                              ], 64 /* STABLE_FRAGMENT */))
                ]),
                _: 1 /* STABLE */
              }, 8 /* PROPS */, ["disabled", "arrow"]))
            : (!isDragging.value)
              ? (_openBlock(), _createBlock(Row, {
                  key: 1,
                  icon: "☝"
                }, {
                  default: _withCtx(() => [
                    _createCommentVNode(" Computed-damage explode (orb near or on shade) "),
                    (tapExplode.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createVNode(Chip, {
                            from: "",
                            tone: "drop",
                            item: item.value.id
                          }, null, 8 /* PROPS */, ["item"]),
                          _createVNode(Chip, {
                            to: "",
                            tone: "warn",
                            icon: "💥"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(explodeDamage(tapExplode.value)), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                          }),
                          _createVNode(Chip, {
                            to: "",
                            tone: "shade",
                            icon: "#"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(explodeCount(tapExplode.value)), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                          })
                        ], 64 /* STABLE_FRAGMENT */))
                      : (item.value.data.shade?.explodeOnShade)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createCommentVNode(" explodeOnShade item with no shade in range — show static, mark\r\n             damage chip disabled to communicate \"can't actually deal damage now\". "),
                            _createVNode(Chip, {
                              from: "",
                              tone: "drop",
                              item: item.value.id
                            }, null, 8 /* PROPS */, ["item"]),
                            _createVNode(Chip, {
                              to: "",
                              tone: "warn",
                              icon: "💥",
                              disabled: ""
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(item.value.data.shade.explodeOnShade.power), 1 /* TEXT */)
                              ]),
                              _: 1 /* STABLE */
                            }),
                            _createVNode(Chip, {
                              to: "",
                              icon: "◯"
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(item.value.data.shade.explodeOnShade.radius), 1 /* TEXT */)
                              ]),
                              _: 1 /* STABLE */
                            })
                          ], 64 /* STABLE_FRAGMENT */))
                        : (tapPreview.value)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
                              _createCommentVNode(" Tap-spawn matched a recipe "),
                              (tapPreview.value.recipe.charge != null)
                                ? (_openBlock(), _createBlock(Chip, {
                                    key: 0,
                                    from: "",
                                    icon: "⚡"
                                  }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(tapPreview.value.recipe.charge * tapPreview.value.chainCount), 1 /* TEXT */)
                                    ]),
                                    _: 1 /* STABLE */
                                  }))
                                : (_openBlock(), _createBlock(Chip, {
                                    key: 1,
                                    from: "",
                                    tone: "drop",
                                    item: item.value.id
                                  }, null, 8 /* PROPS */, ["item"])),
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(countSpawn(tapPreview.value.spawn), (c, id) => {
                                return (_openBlock(), _createBlock(Chip, {
                                  key: `ts${id}`,
                                  to: "",
                                  tone: "spawn",
                                  item: id,
                                  count: c
                                }, null, 8 /* PROPS */, ["item", "count"]))
                              }), 128 /* KEYED_FRAGMENT */))
                            ], 64 /* STABLE_FRAGMENT */))
                          : (defaultTapRecipe.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                                _createCommentVNode(" No matched recipe — show the first tap recipe as static fallback.\r\n             If drops can't fit the board, mark the radius chip disabled. "),
                                (defaultTapRecipe.value.charge != null)
                                  ? (_openBlock(), _createBlock(Chip, {
                                      key: 0,
                                      from: "",
                                      icon: "⚡"
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(defaultTapRecipe.value.charge), 1 /* TEXT */)
                                      ]),
                                      _: 1 /* STABLE */
                                    }))
                                  : (_openBlock(), _createBlock(Chip, {
                                      key: 1,
                                      from: "",
                                      tone: "drop",
                                      item: item.value.id
                                    }, null, 8 /* PROPS */, ["item"])),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resolvedDrops(defaultTapRecipe.value.drops, item.value), (c, id) => {
                                  return (_openBlock(), _createBlock(Chip, {
                                    key: `tr${id}`,
                                    to: "",
                                    tone: "drop",
                                    item: id,
                                    count: c
                                  }, null, 8 /* PROPS */, ["item", "count"]))
                                }), 128 /* KEYED_FRAGMENT */)),
                                _createVNode(Chip, {
                                  tail: "",
                                  icon: "◯",
                                  disabled: !defaultTapFits.value
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(defaultTapRecipe.value.dropRange), 1 /* TEXT */)
                                  ]),
                                  _: 1 /* STABLE */
                                }, 8 /* PROPS */, ["disabled"])
                              ], 64 /* STABLE_FRAGMENT */))
                            : (_openBlock(), _createBlock(Chip, { key: 4 }, {
                                default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                                  _createTextVNode("∅", -1 /* CACHED */)
                                ]))]),
                                _: 1 /* STABLE */
                              }))
                  ]),
                  _: 1 /* STABLE */
                }))
              : _createCommentVNode("v-if", true)
        ]),
        (hasInfo.value)
          ? (_openBlock(), _createElementBlock("hr", _hoisted_6))
          : _createCommentVNode("v-if", true),
        _createCommentVNode(" Info rows: recipes + shade flags. "),
        (hasInfo.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((item.value.data.merge?.recipes ?? []), (r, i) => {
                return (_openBlock(), _createBlock(Row, {
                  key: `mr${i}`,
                  icon: "Σ"
                }, {
                  default: _withCtx(() => [
                    _createVNode(Chip, {
                      from: "",
                      tone: "merge",
                      item: item.value.id,
                      count: r.count
                    }, null, 8 /* PROPS */, ["item", "count"]),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resolvedDrops(r.drops, item.value), (c, id) => {
                      return (_openBlock(), _createBlock(Chip, {
                        key: `mrd${id}`,
                        to: "",
                        tone: "match",
                        item: id,
                        count: c
                      }, null, 8 /* PROPS */, ["item", "count"]))
                    }), 128 /* KEYED_FRAGMENT */))
                  ]),
                  _: 2 /* DYNAMIC */
                }, 1024 /* DYNAMIC_SLOTS */))
              }), 128 /* KEYED_FRAGMENT */)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((item.value.data.charge?.recipes ?? []), (r, i) => {
                return (_openBlock(), _createBlock(Row, {
                  key: `cr${i}`,
                  icon: "⏱"
                }, {
                  default: _withCtx(() => [
                    _createVNode(Chip, {
                      from: "",
                      icon: "⚡"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(r.charge), 1 /* TEXT */)
                      ]),
                      _: 2 /* DYNAMIC */
                    }, 1024 /* DYNAMIC_SLOTS */),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resolvedDrops(r.drops, item.value), (c, id) => {
                      return (_openBlock(), _createBlock(Chip, {
                        key: `crd${id}`,
                        to: "",
                        tone: "spawn",
                        item: id,
                        count: c
                      }, null, 8 /* PROPS */, ["item", "count"]))
                    }), 128 /* KEYED_FRAGMENT */)),
                    _createVNode(Chip, {
                      tail: "",
                      icon: "◯"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(r.dropRange), 1 /* TEXT */)
                      ]),
                      _: 2 /* DYNAMIC */
                    }, 1024 /* DYNAMIC_SLOTS */)
                  ]),
                  _: 2 /* DYNAMIC */
                }, 1024 /* DYNAMIC_SLOTS */))
              }), 128 /* KEYED_FRAGMENT */)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((item.value.data.tap?.recipes ?? []), (r, i) => {
                return (_openBlock(), _createElementBlock(_Fragment, {
                  key: `tr${i}`
                }, [
                  (r.charge != null)
                    ? (_openBlock(), _createBlock(Row, {
                        key: 0,
                        icon: "⚡"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(Chip, {
                            from: "",
                            icon: "⚡"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(r.charge), 1 /* TEXT */)
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1024 /* DYNAMIC_SLOTS */),
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resolvedDrops(r.drops, item.value), (c, id) => {
                            return (_openBlock(), _createBlock(Chip, {
                              key: `trd${id}`,
                              to: "",
                              tone: "drop",
                              item: id,
                              count: c
                            }, null, 8 /* PROPS */, ["item", "count"]))
                          }), 128 /* KEYED_FRAGMENT */)),
                          _createVNode(Chip, {
                            tail: "",
                            icon: "◯"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(r.dropRange), 1 /* TEXT */)
                            ]),
                            _: 2 /* DYNAMIC */
                          }, 1024 /* DYNAMIC_SLOTS */)
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1024 /* DYNAMIC_SLOTS */))
                    : _createCommentVNode("v-if", true)
                ], 64 /* STABLE_FRAGMENT */))
              }), 128 /* KEYED_FRAGMENT */)),
              (item.value.data.shade?.pausedInShade)
                ? (_openBlock(), _createBlock(Row, {
                    key: 0,
                    icon: "🌑"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(Chip, { tone: "shade" }, {
                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                          _createTextVNode("⏸", -1 /* CACHED */)
                        ]))]),
                        _: 1 /* STABLE */
                      })
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              (item.value.data.shade?.explodeOnShade)
                ? (_openBlock(), _createBlock(Row, {
                    key: 1,
                    icon: "🌑"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(Chip, {
                        tone: "warn",
                        icon: "💥"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.value.data.shade.explodeOnShade.power), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      }),
                      _createVNode(Chip, { icon: "◯" }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.value.data.shade.explodeOnShade.radius), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      })
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true),
              (item.value.data.shade?.spawnShade)
                ? (_openBlock(), _createBlock(Row, {
                    key: 2,
                    icon: "🌑"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(Chip, {
                        tone: "shade",
                        icon: "+"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.value.data.shade.spawnShade.amount), 1 /* TEXT */)
                        ]),
                        _: 1 /* STABLE */
                      })
                    ]),
                    _: 1 /* STABLE */
                  }))
                : _createCommentVNode("v-if", true)
            ]))
          : _createCommentVNode("v-if", true)
      ]))
    : _createCommentVNode("v-if", true)
}
}

})